<template lang="pug">
.native-app-notifications-preferences(:class="{'centered': loading}")
  NativeAppSpinnerWrapper(v-if="loading" :active="loading")
  .preferences-list.rogue-scroll-y(v-if="!loading && types.length")
    native-app-list-item(@click="navigateToRapidfire")
      .details
        .title Rapid Fire
        .subtitle Get new product updates.
    native-app-list-item(
      v-for="item in items"
      :key="item.id"
      no-icon
    )
      .details(@click="update(item.id, !model[item.id])")
        .title {{ item.title }}
        .subtitle {{ item.description }}
      Checkbox(:modelValue="model[item.id]" @update:modelValue="update(item.id, $event)")
  NativeAppPlaceholder(
    v-if="!loading && !types.length && prevRoute !== 'nativeAppNotificationsRapidfire'"
    :media-config="{ src: '/Rogue%20App%20Dashboard/Inbox/email_vknkyb.svg', width: 98, height: 60 }"
  )
    p.placeholder-text An error occurred while fetching your notification preferences. Please, try again later.
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NativeAppListItem from './components/native-app-list-item.vue'
import NativeAppSpinnerWrapper from './components/native-app-spinner-wrapper.vue'
import NativeAppPlaceholder from './components/native-app-placeholder.vue'
import debounce from '@/js/helpers/debounce'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useRogue } from '@/js/services/Rogue'

type MessageType = {
  id: number,
  type: string,
  guest: boolean,
  title: string,
  description: string,
}

const customerStore = useCustomerStore()
const rogue = useRogue()
const route = useRoute()
const router = useRouter()

const loading = ref<boolean>(true)
const types = ref<MessageType[]>([])
const model = ref <{ [key: number]: boolean }>({})
const prevRoute = ref<string>('')

const loggedIn = computed<boolean>(() => customerStore.loggedIn)

const items = computed<MessageType[]>(() => {
  const typeItems = types.value.filter(type => type.type !== 'rapidfire')
  if (!loggedIn.value) {
    return typeItems.filter(type => type.guest)
  }

  return typeItems
})

async function load() {
  try {
    const { deviceUid } = rogue.appContext
    const { unsubscribedMessageTypeIds } = await rogue.fetch<{ unsubscribedMessageTypeIds: number[] }>(`/svc_push/preference/list/${deviceUid}`)
    const loadedTypes = await rogue.fetch<MessageType[]>('/svc_push/preference/types')
    types.value = (loadedTypes.length && loadedTypes) || []
    types.value.forEach(type => {
      model.value[type.id] = !unsubscribedMessageTypeIds.includes(type.id)
    })
  } catch (e) {
    rogue.log.error('Unable to get the Rogue App notifications types', e)
  }
  loading.value = false
}

let save: ((...args: any[]) => void) | null = null

function navigateToRapidfire() {
  sessionStorage.setItem('nativeAppNotificationsTypes', JSON.stringify(types.value))
  sessionStorage.setItem('nativeAppNotificationsModel', JSON.stringify(model.value))
  router.push({
    name  : 'nativeAppNotificationsRapidfire',
    query : route.query,
  })
}

function update(id: number, value: boolean) {
  model.value[id] = value
  save!()
}

watch(loggedIn, () => {
  load()
})

onMounted(() => {
  load()

  save = debounce(() => {
    const unsubscribedMessageTypeIds: number[] = []
    types.value.forEach(item => {
      if (!model.value[item.id]) {
        unsubscribedMessageTypeIds.push(item.id)
      }
    })
    const { deviceUid } = rogue.appContext
    rogue.fetch('/svc_push/preference', 'POST', { uid: deviceUid, unsubscribedMessageTypeIds })
  }, 1000)
})
</script>

<style scoped lang="scss">
.native-app-notifications-preferences.centered {
  display: flex;
  align-items: center;
  justify-content: center;
}
.native-app-list-item {
  padding: $space-xxs $space-xxs $space-xxs $space-xs;
}
.title {
  font-size: $size-s;
  line-height: $line-height-l;
}
.subtitle {
  font-size: $size-xxs;
  color: $color-gray-dark;
}
.placeholder {
  height: 100vh;

  :deep(.placeholder-icon) {
    height: 60px;
    width: 98px;

    img {
      max-height: 60px;
    }
  }
}

.preferences-list {
  display: flex;
  flex-direction: column;
  gap: $space-xxxs;
  padding: $space-s;
}
</style>
